// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useMutation } from "@apollo/client"
import { Box, Button, Checkbox, Flex, useToast } from "@chakra-ui/react"
import { useAtom } from "jotai"
import { useEffect, useRef, useState } from "react"
import { LOCAL_STORAGE_REGISTED_EMAIL } from "../../../common/commonConstants"
import { signupPageAtom } from "../../../components/Menu/menu.store"
import { Heading3WhiteUnderlined } from "../../../components/commons/Heading3WhiteUnderlined"
import { type SignupFormDataInterface } from "../../../components/commons/commonTypes"
import { FormElement, formBoxStyleProps } from "../../../components/commons/globalFormComponent"
import { chakraTheme } from "../../../theme/chakraTheme"
import { REGISTER, validateRequiredForm } from "./signup.support"
import registerFormJSON from "./signupForm.json"

export const SignUpMain = ({
  successCallBack,
  showOptionalValuesCallBack,
  onBackCallback,
  headingText
}: {
  successCallBack: () => void
  showOptionalValuesCallBack: () => void
  onBackCallback: () => void
  headingText?: string
}): JSX.Element => {
  const toast = useToast()
  const [termsAndConditionsCheck, setTermsAndConditionsCheck] = useState<boolean>(false)
  const [showOptionalFields, setshowOptionalFields] = useState<boolean>(false)
  const signupForm = useRef<HTMLFormElement>(null)
  const { signup, optionalFields } = registerFormJSON as {
    signup: SignupFormDataInterface[]
    optionalFields: SignupFormDataInterface[]
  }
  const [getSignupPageAtom, setSignupPageAtom] = useAtom(signupPageAtom)

  const [registerUser, { data: registerUserMutation, loading, error }] = useMutation(REGISTER)
  const submitSignUpForm = (e: React.FormEvent): void => {
    registerUser({ variables: getSignupPageAtom }).catch(() => {
      // Error handled by Apollo Client
    })

    e.preventDefault()
  }

  const handleTermsClick = (): void => {
    setTermsAndConditionsCheck(!termsAndConditionsCheck)
  }

  // Required Form Validation
  const handleSignupButtonClick = (): void => {
    const { isValid } = validateRequiredForm(signupForm.current, getSignupPageAtom, termsAndConditionsCheck, toast)
    if (isValid) {
      setshowOptionalFields(true)
      showOptionalValuesCallBack()
    }
  }

  // Form data assignment
  const handleFormElementChange = (value: string, name: string): void => {
    setSignupPageAtom(prev => ({
      ...prev,
      [name]: value
    }))
  }

  useEffect(() => {
    if (registerUserMutation) {
      successCallBack()
      if (getSignupPageAtom.email_address) {
        localStorage.setItem(LOCAL_STORAGE_REGISTED_EMAIL, getSignupPageAtom.email_address)
      }
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (error?.graphQLErrors?.length || error?.message) {
      toast({
        title: "Error",
        description: (error.graphQLErrors[0] as unknown as string) ?? `Resend Error: ${error?.message}`,
        status: "error"
      })
    }
  }, [registerUserMutation, error, getSignupPageAtom.email_address, successCallBack, toast])
  useEffect(() => {
    setSignupPageAtom({})
  }, [setSignupPageAtom])

  return (
    <Flex align="center" justify={"center"} flexDir="column">
      <Heading3WhiteUnderlined props={{ color: "white" }} text={headingText ?? "Or create an account with us"} />

      <Flex {...formBoxStyleProps} align="center" maxWidth="313px" px="26px" pt="0">
        <form style={{ width: "100%" }} name="registrationForm" method="post" ref={signupForm} onSubmit={submitSignUpForm}>
          <Box w="100%">
            {signup?.map(
              (
                {
                  name,
                  type,
                  placeHolder,
                  label,
                  htmlValidation,
                  customSelectOptions,
                  formLabelOptionalTextProps,
                  formLabelTextProps
                }: SignupFormDataInterface,
                index
              ) =>
                (!showOptionalFields || (showOptionalFields && name !== "re_enter_email" && name !== "re_enter_password")) && (
                  <FormElement
                    key={name}
                    name={name}
                    type={type}
                    placeHolder={placeHolder}
                    label={name === "password" && showOptionalFields ? "Your Password" : label}
                    requiredText="*"
                    value={(getSignupPageAtom[name] as string) ?? ""}
                    onChangeCallBack={(value: string) => {
                      handleFormElementChange(value, name)
                    }}
                    htmlValidation={htmlValidation}
                    formLabelOptionalTextProps={formLabelOptionalTextProps}
                    customSelectOptions={customSelectOptions}
                    showEditIcon={!!showOptionalFields}
                    formLabelTextProps={formLabelTextProps}
                    formControlProps={{ mt: index === 0 ? "32px" : "0", px: 0 }}
                  />
                )
            )}
          </Box>

          {!showOptionalFields && (
            <Flex
              align={"center"}
              flexDir={"column"}
              color="white"
              w="313px"
              pos={"relative"}
              left="-29px"
              top="16px"
              mb={["28px", "28px", "66px", "66px"]}
            >
              <Button variant="Button1RegularOutline" mt="0" isLoading={loading} onClick={handleSignupButtonClick} type="button">
                Sign Up
              </Button>
              <Checkbox
                mt="16px"
                display={"flex"}
                alignItems={"flex-start"}
                color="white"
                isChecked={termsAndConditionsCheck}
                onChange={handleTermsClick}
                {...chakraTheme.CheckBoxText}
              >
                <span
                  style={{
                    fontSize: "15px",
                    lineHeight: "19px",
                    fontFamily: "PT Sans",
                    fontWeight: "400",
                    position: "relative",
                    top: "-3px",
                    width: "235px",
                    display: "inline-block"
                  }}
                >
                  I have read and accept the terms and conditions.
                </span>
              </Checkbox>

              <Box mt="32px" h="24px" lineHeight="24px">
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  style={{
                    height: "18px",
                    color: "white",
                    fontWeight: "bold",
                    textDecoration: "none",
                    display: "inline-block"
                  }}
                >
                  View Terms & Conditions
                </a>
              </Box>
              <Box mt="4px" h="24px">
                <a
                  href="/terms-and-conditions?privacy-policy"
                  target="_blank"
                  style={{
                    height: "18px",
                    color: "white",
                    fontWeight: "bold",
                    textDecoration: "none",
                    display: "inline-block"
                  }}
                >
                  View Privacy Policy
                </a>
              </Box>
            </Flex>
          )}
          {showOptionalFields && (
            <>
              <Flex mt="32px" flexWrap={"wrap"} mb={["28px", "28px", "32px", "32px"]}>
                {optionalFields?.map(
                  (
                    {
                      name,
                      type,
                      placeHolder,
                      label,
                      htmlValidation,
                      inputStyles,
                      formControlProps,
                      customSelectOptions,
                      formLabelTextProps,
                      formLabelOptionalTextProps
                    }: SignupFormDataInterface,
                    index
                  ) => (
                    <FormElement
                      key={name}
                      name={name}
                      type={type}
                      placeHolder={placeHolder}
                      label={label}
                      value={(getSignupPageAtom[name] as string) ?? ""}
                      onChangeCallBack={(value: string) => {
                        handleFormElementChange(value, name)
                      }}
                      htmlValidation={htmlValidation}
                      inputStyles={inputStyles}
                      formControlProps={{ ...formControlProps, mb: index === optionalFields.length - 1 ? "0" : "16px" }}
                      customSelectOptions={customSelectOptions}
                      formLabelTextProps={formLabelTextProps}
                      formLabelOptionalTextProps={formLabelOptionalTextProps}
                    />
                  )
                )}
              </Flex>
              <Flex w="255px" justify={"center"} mb={["24px", "24px", "66px", "66px"]}>
                <Button
                  _hover={{ bg: "transparent" }}
                  variant={"whiteLink"}
                  onClick={onBackCallback}
                  mr="25px"
                  fontFamily="PT Sans"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="700"
                  lineHeight="17px"
                >
                  Back
                </Button>
                <Button _hover={{ bg: "white" }} variant="Button1RegularOutline1" type="submit">
                  Confirm
                </Button>
              </Flex>
            </>
          )}
        </form>
      </Flex>
    </Flex>
  )
}
